import { Box, Flex, Heading, Icon, Stack, Text, useBreakpointValue, VStack } from '@chakra-ui/react'
import * as React from 'react'
import { Skeleton } from '@chakra-ui/react'
import Card from '../Card'
import { VscLink } from 'react-icons/vsc'

export type StatType = {
  title: string
  icon: any
  loading?: boolean
  value: string
  onClick?: () => void
}
type Props = {
  label?: string
  icon?: React.ReactNode
  loading?: boolean
  value: string | number | StatType
  type?: 'primary' | 'secondary' | 'stat'
  onClick?: () => void
}
const Stat = (props: Props) => {
  const { label, value, icon, loading, type, ...boxProps } = props
  const stat = value as StatType
  // Hoisted out of the JSX: it used to be called inside the non-`stat` branch
  // only, so a `Stat` whose `type` prop changed between renders swapped the
  // hook in and out and crashed React's hook bookkeeping.
  const headingSize = useBreakpointValue({ base: 'xs', md: 'sm' })
  return type === 'stat' ? (
    <Skeleton
      isLoaded={!stat.loading}
      rounded="0"
      cursor={stat.onClick ? 'pointer' : 'default'}
      onClick={stat.onClick}
    >
      <Card
        p={4}
        position="relative"
        _hover={{ borderColor: 'gray.300' }}
        border="1px solid"
        borderColor="gray.100"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          transform="translate(-50%,-50%)"
          boxSize={8}
          alignItems="center"
          display="flex"
          justifyContent="center"
          rounded="0"
          border="2px solid"
          borderColor="white"
          bg="gray.100"
          p={2}
          zIndex={4}
        >
          <Icon as={stat.icon} boxSize={4} color="gray.600" />
        </Box>

        <VStack spacing={0} alignItems="flex-start">
          <Text
            color="gray.500"
            isTruncated
            fontWeight="600"
            fontSize="2xs"
            textTransform="uppercase"
            letterSpacing="0.06em"
          >
            {stat.title}
          </Text>

          <Text fontWeight="500" isTruncated color="gray.900">
            {stat.value.split(' ')[0]}{' '}
            <Text as="span" color="gray.500" fontWeight="normal" fontSize="xs">
              {stat.value.split(' ').slice(1).join(' ').toLocaleLowerCase() || ''}
            </Text>
          </Text>
        </VStack>
        {stat.onClick && (
          <Box
            position="absolute"
            bottom={0}
            left="50%"
            transform="translate(-50%,50%)"
            boxSize={8}
            alignItems="center"
            display="flex"
            justifyContent="center"
            rounded="0"
            border="2px solid"
            borderColor="white"
            bg="gray.100"
            p={2}
          >
            <Icon as={VscLink} boxSize={4} color="gray.600" />
          </Box>
        )}
      </Card>
    </Skeleton>
  ) : (
    <Box bg="bg-surface" borderRadius="0" {...boxProps}>
      <Stack textAlign="center">
        <Skeleton isLoaded={!loading}>
          <Flex align="center">
            {icon}
            <Text
              ml={1}
              fontSize="2xs"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="0.04em"
              fontWeight="500"
            >
              {label}
            </Text>
          </Flex>
        </Skeleton>
        <Skeleton isLoaded={!loading}>
          <Heading size={headingSize} fontWeight="400" letterSpacing="-0.02em">
            {String(value)}
          </Heading>
        </Skeleton>
      </Stack>
    </Box>
  )
}
export default Stat
